import React from 'react';
import { Flex, Tabs, type TabsProps, Typography, theme } from 'antd';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import requireAuth from '~components/hocs/requireAuth';
import { AiInsightsLearnMoreLink, CrawlerUpdateAnnouncement, hasAiInsightsAccess } from '../features/ai-insights';
import AdminTemplate from '../layout/AdminTemplate';

const { Title, Paragraph } = Typography;

const META_TAGS = {
  title: 'AI Insights - Prerender.io',
};

const TAB_KEYS = {
  discoverability: 'discoverability',
  diagnostics: 'diagnostics',
} as const;

type TabKey = (typeof TAB_KEYS)[keyof typeof TAB_KEYS];

const TAB_ITEMS: TabsProps['items'] = [
  { key: TAB_KEYS.discoverability, label: 'Discoverability' },
  { key: TAB_KEYS.diagnostics, label: 'Diagnostics' },
];

function AIInsightsLayout() {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const activeTab: TabKey = React.useMemo(() => {
    const segment = pathname.split('/ai-insights/').pop()?.split('/')[0];
    if (segment === TAB_KEYS.diagnostics) return TAB_KEYS.diagnostics;
    return TAB_KEYS.discoverability;
  }, [pathname]);

  const onTabChange: TabsProps['onChange'] = (key) => {
    navigate(`/ai-insights/${key}`);
  };

  return (
    <AdminTemplate metaTags={META_TAGS}>
      <Title
        level={2}
        style={{
          fontSize: token.fontSizeHeading2,
          fontWeight: token.fontWeightStrong,
          marginTop: 0,
          marginBottom: token.marginXS,
        }}
      >
        AI Insights
      </Title>
      <Paragraph
        style={{
          fontSize: token.fontSize,
          color: token.colorTextSecondary,
          marginTop: 0,
          marginBottom: token.marginXS,
        }}
      >
        Track AI Crawler access, spot discoverability gaps, and fix issues in real time.
      </Paragraph>
      <Flex style={{ marginBottom: token.marginLG }}>
        <AiInsightsLearnMoreLink />
      </Flex>
      <CrawlerUpdateAnnouncement />
      <Tabs items={TAB_ITEMS} activeKey={activeTab} onChange={onTabChange} />
      <Outlet />
    </AdminTemplate>
  );
}

export default requireAuth(AIInsightsLayout, hasAiInsightsAccess);
